import { CreditCard } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { formatWithThousandSeparator } from '@/utils';
import { SectionHeader } from './SectionHeader';

interface CostSummarySectionProps {
  products: Array<{ title?: string; price: number | string; quantity?: number }>;
  shippingPrice: number;
  total: number;
  currency: string;
}

function formatAmount(amount: number | string, currency: string): string {
  const formatted = formatWithThousandSeparator(amount);
  if (formatted === ' - ') return `— ${currency}`;
  return `${formatted} ${currency}`;
}

export function CostSummarySection({
  products,
  shippingPrice,
  total,
  currency,
}: CostSummarySectionProps) {
  const { t } = useTranslation();

  return (
    <div className="rounded-2xl border border-sd-border bg-sd-card p-5">
      <SectionHeader icon={CreditCard} title={String(t('cost_summary'))} />

      <div className="divide-y divide-sd-border">
        {products.map((product, index) => (
          <div key={index} className="flex items-center justify-between py-2">
            <span className="text-sm text-sd-muted-foreground truncate min-w-0 me-3">
              {product.title ??
                `${String(t('products'))}${product.quantity != null ? ` ×${product.quantity}` : ''}`}
            </span>
            <span className="text-sm font-medium shrink-0">
              {formatAmount(product.price, currency)}
            </span>
          </div>
        ))}

        <div className="flex items-center justify-between py-2">
          <span className="text-sm text-sd-muted-foreground">{String(t('shipping'))}</span>
          <span className="text-sm font-medium shrink-0">
            {formatAmount(shippingPrice, currency)}
          </span>
        </div>

        <div className="flex items-center justify-between py-2.5">
          <span className="text-sm font-bold">{String(t('total'))}</span>
          <span className="text-sm font-bold shrink-0">{formatAmount(total, currency)}</span>
        </div>
      </div>
    </div>
  );
}
